import React, { useState } from "react";
import { X, Loader2, UserPlus } from "lucide-react";
import toast from "react-hot-toast";
import { updatePropertyBuyer } from "../../../api/propertyManagement";

/**
 * Edit a buyer attached to a property's Buyers tab — the link note plus the
 * underlying contact's name/email/phone. On success calls onSaved(updatedBuyer)
 * with the refreshed server payload so the parent can update its list in place.
 *
 * Self-contained overlay so both detail pages can reuse it.
 */
export default function EditBuyerModal({ propertyId, buyer, onClose, onSaved, isLease = false }) {
  const personLabel = isLease ? "renter" : "buyer";
  const personLabelCap = isLease ? "Renter" : "Buyer";
  const [form, setForm] = useState({
    name: buyer?.name || "",
    email: buyer?.email || "",
    phone: buyer?.phone || "",
    notes: buyer?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Please enter a name.");
      return;
    }
    setSaving(true);
    try {
      const res = await updatePropertyBuyer(propertyId, buyer.link_id, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        notes: form.notes.trim(),
      });
      toast.success(`${personLabelCap} updated.`);
      onSaved?.(res?.data || { ...buyer, ...form });
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.detail || `Could not update ${personLabel}.`);
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]";

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[92vh] flex flex-col my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-[#55C5D0]" />
            <h2 className="text-base font-semibold text-gray-900">Edit {personLabelCap}</h2>
          </div>
          <button onClick={onClose} aria-label="Close" className="-mr-1 p-1 text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div>
            <label className="text-xs font-bold text-[#374151] block mb-1">Name *</label>
            <input value={form.name} onChange={set("name")} className={inputCls} autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-[#374151] block mb-1">Email</label>
              <input type="email" value={form.email} onChange={set("email")} className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-bold text-[#374151] block mb-1">Phone</label>
              <input value={form.phone} onChange={set("phone")} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-[#374151] block mb-1">Note</label>
            <textarea value={form.notes} onChange={set("notes")} rows={3} placeholder="e.g. Called 5 Jun — keen, wants a second inspection" className={`${inputCls} resize-none`} />
          </div>
          <p className="text-[11px] text-gray-400">
            Editing name/email/phone updates this {personLabel}'s contact record across the CRM.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t bg-slate-50 rounded-b-2xl">
          <button type="button" onClick={onClose} disabled={saving}
            className="text-xs px-3 py-2 border rounded-md bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="text-xs flex items-center gap-1 px-4 py-2 bg-[#55C5D0] hover:bg-[#47b1bb] disabled:opacity-50 text-white rounded-md font-bold uppercase tracking-wider">
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
